import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { isUndefined, isNull } from 'lodash';

import { BenefitLimit } from '@models/BenefitLimit';
import { BenefitTemplateTerm } from '@models/BenefitTemplateTerm';
import { formatDate } from '@utils/datetime';
import { formatCurrency } from '@utils/currency';
import { BackButton, Loading, LoadingError } from '@components/Base';

import { useInsurancePolicyUserFetch } from './hook';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const InsurancePolicyUserDetailsPage = () => {
  const { t } = useTranslation();
  const policyUser = useInsurancePolicyUserFetch();

  if (isUndefined(policyUser)) return <Loading />;
  if (isNull(policyUser)) return <LoadingError />;

  const {
    name,
    memberId,
    effectiveDate,
    expirationDate,
    insurancePolicy,
    benefitTemplate,
    memberCard,
    memberCardKm,
  } = policyUser;

  const benefitLimitsColumns: TableColumn<BenefitLimit>[] = [
    {
      name: t('common:benefit'),
      selector: (row) => row.benefit,
      sortable: true,
      wrap: true,
    },
    {
      name: t('common:subBenefit'),
      selector: (row) => row.subBenefit,
      sortable: true,
      wrap: true,
    },
    {
      name: t('common:maxLimitAmount'),
      cell: (row) => formatCurrency(row.maxLimitAmount),
      sortable: true,
    },
    {
      name: t('common:description'),
      selector: (row) => row.description,
      sortable: true,
      wrap: true,
    },
  ];

  const benefitTemplateTermsColumns: TableColumn<BenefitTemplateTerm>[] = [
    {
      name: t('user.name'),
      selector: (row) => row.insuranceTerm?.description || '',
      sortable: true,
      wrap: true,
    },
    {
      name: t('common:description'),
      selector: (row) => row.description,
      sortable: true,
      wrap: true,
    },
  ];

  return (
    <Container className="my-4">
      <Row className="mb-3">
        <Col>
          <BackButton />
        </Col>
      </Row>

      <Row className="mb-4 align-items-center">
        <Col>
          <h2>{t('policyDetails.title')}</h2>
        </Col>
        <Col xs="auto">
          {memberCard && (
            <a href={memberCard} target="_blank" rel="noopener noreferrer" className="btn btn-primary me-2">
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              {t('policyDetails.printDigitalCard')} (EN)
            </a>
          )}
          {memberCardKm && (
            <a href={memberCardKm} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              {t('policyDetails.printDigitalCard')} (KM)
            </a>
          )}
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">{t('common:policyInformation')}</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('policyDetails.memberName')}:</strong>
              <div>{name}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:memberId')}:</strong>
              <div>{memberId}</div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:policyNumber')}:</strong>
              <div>{insurancePolicy.policyNumber}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:plan')}:</strong>
              <div>{benefitTemplate.name}</div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:effectiveDate')}:</strong>
              <div>{formatDate(effectiveDate)}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:endDate')}:</strong>
              <div>{formatDate(expirationDate)}</div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('digitalCard.annualLimit')}:</strong>
              <div>{formatCurrency(benefitTemplate.annualLimitAmount)}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('digitalCard.geographicCoverage')}:</strong>
              <div>{benefitTemplate.geographicCoverage}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">{t('common:coverage')}</h5>
        </Card.Header>
        <Card.Body>
          <DataTable
            columns={benefitLimitsColumns}
            data={policyUser.benefitTemplate.benefitLimits || []}
            customStyles={customStyles}
            pagination
            fixedHeader
            noDataComponent={<div className="p-3">{t('common:noData')}</div>}
          />
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">{t('common:termsAndConditions')}</h5>
        </Card.Header>
        <Card.Body>
          <DataTable
            columns={benefitTemplateTermsColumns}
            data={benefitTemplate.benefitTemplateTerms || []}
            customStyles={customStyles}
            pagination
            fixedHeader
            noDataComponent={<div className="p-3">{t('common:noData')}</div>}
          />
        </Card.Body>
      </Card>

    </Container>
  );
};

export { InsurancePolicyUserDetailsPage };
